import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ApiErrorAlert from '../common/ApiErrorAlert';
import AuthPageShell, {
  authInputClass,
  authLabelClass,
  authPrimaryButtonClass,
} from './AuthPageShell';

const Login: React.FC = () => {
  const [emailOrMobile, setEmailOrMobile] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.verificationSuccess || location.state?.registrationSuccess) {
      setSuccessMessage(location.state.message);
      if (location.state.email) {
        setEmailOrMobile(location.state.email);
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    
    try {
      const result = await login(emailOrMobile, password);
      if (result.success) {
        const state = location.state as { from?: string; redirectTo?: string } | null;
        const from = state?.redirectTo || state?.from;
        const shouldResume =
          !!from &&
          (from.startsWith('/checkout') ||
            from.startsWith('/course') ||
            from.startsWith('/unit') ||
            from.startsWith('/payment') ||
            from.startsWith('/courses/') ||
            from.startsWith('/student-portal') ||
            from.startsWith('/profile'));
        navigate(shouldResume ? from : '/student-portal', { replace: true });
      } else {
        setError(result.error || 'فشل تسجيل الدخول');
      }
    } catch (error) {
      setError('حدث خطأ غير متوقع');
    }
  };

  const handleRetry = () => {
    setError('');
  };

  return (
    <AuthPageShell
      eyebrow="تسجيل الدخول"
      title="مرحباً بعودتك"
      description="سجّل دخولك عشان تكمل رحلة التعلم من حيث وقفت"
      footer={
        <>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-brand-100" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 text-slate-500">جديد على المنصة؟</span>
            </div>
          </div>
          <div className="mt-5 text-center">
            <Link
              to="/register"
              className="text-sm font-semibold text-brand-600 transition-colors hover:text-brand-700"
            >
              إنشاء حساب جديد
            </Link>
          </div>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && <ApiErrorAlert message={error} onRetry={handleRetry} />}

        {successMessage && (
          <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm font-medium text-brand-800">
            {successMessage}
          </div>
        )}

        <div>
          <label htmlFor="emailOrMobile" className={authLabelClass}>
            البريد الإلكتروني أو رقم الجوال
          </label>
          <input
            id="emailOrMobile"
            name="emailOrMobile"
            type="text"
            autoComplete="username"
            required
            value={emailOrMobile}
            onChange={(e) => setEmailOrMobile(e.target.value)}
            className={authInputClass}
            placeholder="أدخل بريدك الإلكتروني أو رقم الجوال"
          />
        </div>

        <div>
          <label htmlFor="password" className={authLabelClass}>
            كلمة المرور
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={authInputClass}
            placeholder="أدخل كلمة المرور"
          />
        </div>

        <div className="flex justify-start">
          <Link
            to="/forgot-password"
            className="text-sm font-semibold text-brand-600 hover:text-brand-700"
          >
            نسيت كلمة المرور؟
          </Link>
        </div>

        <button type="submit" disabled={isLoading} className={authPrimaryButtonClass}>
          {isLoading ? (
            <span className="inline-flex items-center gap-2">
              <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              جاري تسجيل الدخول...
            </span>
          ) : (
            'تسجيل الدخول'
          )}
        </button>
      </form>
    </AuthPageShell>
  );
};

export default Login; 